import { apiFetch } from './client';
import type { PortfolioSectionKind, TemplateId, ThemeId, FontPairId } from '@/types/portfolio';
import type { PreviewDraft } from '@/features/editor/preview/LivePreview';

export interface PortfolioState {
  readonly template: TemplateId;
  readonly theme: ThemeId;
  readonly fontPair: FontPairId;
  readonly enabledSections: ReadonlyArray<PortfolioSectionKind>;
  readonly draft: PreviewDraft;
  readonly published: PreviewDraft | null;
  readonly publishedAt: string | null;
  readonly updatedAt: string;
}

export interface PortfolioSettingsPatch {
  template?: TemplateId;
  theme?: ThemeId;
  fontPair?: FontPairId;
  enabledSections?: ReadonlyArray<PortfolioSectionKind>;
}

export interface PortfolioRevision {
  readonly id: string;
  readonly publishedAt: string;
  readonly publishedBy: string;
}

export const portfolioApi = {
  get(): Promise<PortfolioState> {
    return apiFetch<PortfolioState>('/portfolio');
  },

  updateSettings(patch: PortfolioSettingsPatch): Promise<PortfolioState> {
    return apiFetch<PortfolioState>('/portfolio/settings', {
      method: 'PATCH',
      body: patch,
    });
  },

  upsertSection<K extends PortfolioSectionKind>(
    kind: K,
    body: NonNullable<PreviewDraft[K]>,
  ): Promise<PortfolioState> {
    return apiFetch<PortfolioState>(`/portfolio/section/${kind}`, {
      method: 'PUT',
      body,
    });
  },

  deleteSection(kind: PortfolioSectionKind): Promise<PortfolioState> {
    return apiFetch<PortfolioState>(`/portfolio/section/${kind}`, { method: 'DELETE' });
  },

  publish(): Promise<PortfolioState> {
    return apiFetch<PortfolioState>('/portfolio/publish', { method: 'POST' });
  },

  unpublish(): Promise<PortfolioState> {
    return apiFetch<PortfolioState>('/portfolio/unpublish', { method: 'POST' });
  },

  listRevisions(cursor?: string): Promise<{ items: PortfolioRevision[]; nextCursor?: string }> {
    return apiFetch('/portfolio/revisions', { searchParams: { cursor } });
  },

  restoreRevision(id: string): Promise<PortfolioState> {
    return apiFetch<PortfolioState>(`/portfolio/revisions/${id}/restore`, { method: 'POST' });
  },
};
